import React, { useMemo } from 'react';

import { FieldProps, FormikErrors, getIn } from 'formik';

import { ITextAreaProps } from '@rbi-ctg/frontend';
import IconCheck from 'components/icons/check';
import useIdCounter from 'hooks/use-id-counter';
import { FormValidationState } from 'utils/form';

import {
  FloatingLabelInputWrapper,
  IconContainer,
  Message,
  StyledTextArea,
  TextAreaLabel,
} from './styled';
import useTestIds from './use-test-ids';
import { useTextAreaHandles } from './use-text-area-handles';

export interface IFloatingLabelTextAreaProps<E = any> extends ITextAreaProps {
  label: string;
  labelId?: string;
  message?: string | FormikErrors<E>;
  validation: FormValidationState;
  [key: string]: any;
}

export const FloatingLabelTextArea: React.FC<IFloatingLabelTextAreaProps> = ({
  'data-testid': testId,
  onBlur,
  onFocus,
  label,
  value,
  validation,
  maxLength,
  message,
  onChange,
  className,
  autoComplete,
  pattern,
  labelId,
  readOnly = false,
  ...rest
}) => {
  const textAreaId = useIdCounter('floating-label-text-area');

  const messageId = useIdCounter('message');

  const { handleBlur, handleChange, handleFocus } = useTextAreaHandles({
    textAreaId,
    onBlur,
    onChange,
    onFocus,
  });

  const { wrapperTestId, labelTestId, validationTestId, messageTestId } = useTestIds(testId);

  const textAreaProps = useMemo(
    () => ({
      'aria-labelledby': labelId,
      autoComplete,
      'data-testid': testId,
      id: textAreaId,
      maxLength,
      onBlur: handleBlur,
      onChange: handleChange,
      onFocus: handleFocus,
      pattern,
      readOnly,
      disabled: readOnly,
      type: 'text',
      validation,
      value,
    }),
    [
      autoComplete,
      handleBlur,
      handleChange,
      handleFocus,
      textAreaId,
      labelId,
      pattern,
      readOnly,
      testId,
      validation,
      value,
      maxLength,
    ]
  );

  return (
    <FloatingLabelInputWrapper className={className} data-testid={wrapperTestId}>
      <StyledTextArea {...textAreaProps} {...rest} data-private />
      <TextAreaLabel
        id={labelId}
        htmlFor={textAreaId}
        hasValue={typeof value === 'number' || !!value}
        data-testid={labelTestId}
      >
        {label}
      </TextAreaLabel>
      <IconContainer validation={validation} data-testid={validationTestId}>
        {validation === FormValidationState.VALID && (
          <IconCheck data-testid="input-validation-valid" />
        )}
      </IconContainer>
      {message && (
        <Message id={messageId} data-testid={messageTestId} validation={validation}>
          {message}
        </Message>
      )}
    </FloatingLabelInputWrapper>
  );
};

export const FormikFloatingLabelTextArea = ({
  field,
  form,
  ...props
}: FieldProps & IFloatingLabelTextAreaProps) => {
  const { errors, touched } = form;

  const fieldTouched = getIn(touched, field.name);
  const error = getIn(errors, field.name);
  const validation = error ? FormValidationState.INVALID : FormValidationState.VALID;

  return (
    <FloatingLabelTextArea
      {...field}
      message={fieldTouched ? error : undefined}
      // @ts-ignore
      validation={fieldTouched && validation}
      {...props}
    />
  );
};
